import {Injectable, inject} from '@angular/core';
import {UserStatus} from '@evencel/models';
import {Navigate} from '@ngxs/router-plugin';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {StateReset} from 'ngxs-reset-plugin';
import {delay, finalize, mergeMap, tap} from 'rxjs';
import {LoginService} from '../../services/login/login.service';
import {AppState} from '../app';
import {HandleTokenResponse, ShowLoading, UpdateCenterRoles} from '../app/app.actions';
import {ChangePassword, ForgotPassword, GetPolicies, Logout, ResetPassword, SignIn, SignOut} from './login.actions';

@State<void>({name: 'login'})
@Injectable({providedIn: 'root'})
export class LoginState {
    store = inject(Store);

    constructor(private loginService: LoginService) {}

    @Action(SignIn)
    signIn({dispatch}: StateContext<void>, {credentials}: SignIn) {
        dispatch(new ShowLoading(true));
        return this.loginService.login(credentials).pipe(
            mergeMap(res => dispatch(new HandleTokenResponse(res))),
            mergeMap(() => dispatch(new GetPolicies())),
            mergeMap(() => this.store.selectOnce(AppState.user)),
            tap(user => {
                if (user?.status === UserStatus.CREATED || user?.status === UserStatus.FORGOT_PASSWORD) {
                    return dispatch(new Navigate(['/change-password']));
                }
                return dispatch(new Navigate(['/admin/dashboard']));
            }),
            finalize(() => dispatch(new ShowLoading(false)))
        );
    }

    @Action(SignOut)
    signOut({dispatch}: StateContext<void>) {
        dispatch(new ShowLoading(true));
        return this.loginService.logout().pipe(
            tap(() => dispatch(new Navigate(['/']))),
            delay(500),
            finalize(() => dispatch([new StateReset(AppState), new ShowLoading(false)]))
        );
    }

    @Action(Logout)
    logOut({dispatch}: StateContext<void>) {
        return dispatch(new ShowLoading(true)).pipe(
            tap(() => dispatch(new Navigate(['/']))),
            delay(500),
            finalize(() => dispatch([new StateReset(AppState), new ShowLoading(false)]))
        );
    }

    @Action(ResetPassword)
    resetPassword({dispatch}: StateContext<void>, {resetPassword}: ResetPassword) {
        dispatch(new ShowLoading(true));
        return this.loginService.resetPassword(resetPassword).pipe(
            // tap(() => dispatch(new Navigate(['/']))),
            finalize(() => dispatch(new ShowLoading(false)))
        );
    }

    @Action(ChangePassword)
    changePassword({dispatch}: StateContext<void>, {changePassword, qp}: ChangePassword) {
        return this.loginService.changePassword(changePassword, qp).pipe(
            mergeMap(res => dispatch(new HandleTokenResponse(res))),
            mergeMap(() => dispatch(new GetPolicies())),
            finalize(() => dispatch(new ShowLoading(false)))
        );
    }

    @Action(GetPolicies)
    getPolicies({dispatch}: StateContext<void>) {
        return this.loginService.getPolicies().pipe(mergeMap(cr => dispatch(new UpdateCenterRoles(cr))));
    }

    @Action(ForgotPassword)
    forgotPassword({dispatch}: StateContext<void>, {iForgotPassword}: ForgotPassword) {
        dispatch(new ShowLoading(true));
        return this.loginService.forgotPassword(iForgotPassword).pipe(
            tap(() => dispatch(new Navigate(['/']))),
            finalize(() => dispatch(new ShowLoading(false)))
        );
    }
}
